/**
 * Catalog-domain seam: packages, prices, add-ons, coupons, categories, and the
 * public pricing-page packages. Wraps the mock consts behind hooks so the
 * mock→real flip is an env change (VITE_USE_MOCKS).
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGetList, apiPost, apiPatch } from './client';
import { packages, pricesByPackage, proPrices, coupons, addons, pricingPagePackages } from '../mocks/pricing';
import type { Price } from '../mocks/pricing';
import { packageCategories } from '../mocks/clientBatch';

export const listPackages = () =>
  USE_MOCKS ? mockDelay(packages) : apiGetList<(typeof packages)[number]>('/packages');
// Flat list (each Price already carries packageId) — the GET /prices shape.
export const getPricesByPackage = () =>
  USE_MOCKS ? mockDelay(Object.values(pricesByPackage).flat()) : apiGetList<Price>('/prices');

/* ── Buyer-facing catalog (host-scoped) ────────────────────────────────────────
 * The plan picker on /businesses/new must offer the packages of the catalog the
 * visitor's DOMAIN sells — a reseller's customer buys the RESELLER's plans, not
 * ITCity's. This CANNOT be filtered client-side: the public Package DTO's
 * `soldBy` says only *whether* a package is reseller-owned, never *which*
 * reseller. So the host goes to the server, exactly as /pricing already does. */
const hostQuery = () =>
  `?host=${encodeURIComponent(typeof window !== 'undefined' ? window.location.host : '')}`;
export const listSellablePackages = () =>
  USE_MOCKS ? mockDelay(packages) : apiGetList<(typeof packages)[number]>(`/packages${hostQuery()}`);
export const listSellablePrices = () =>
  USE_MOCKS ? mockDelay(Object.values(pricesByPackage).flat()) : apiGetList<Price>(`/prices${hostQuery()}`);
export const getProPrices = () =>
  USE_MOCKS ? mockDelay(proPrices) : apiGetList<(typeof proPrices)[number]>('/prices/pro');
export const listCoupons = () =>
  USE_MOCKS ? mockDelay(coupons) : apiGetList<(typeof coupons)[number]>('/coupons');
export const listAddons = () =>
  USE_MOCKS ? mockDelay(addons) : apiGetList<(typeof addons)[number]>('/add-ons');
// Host-aware (Decision #21): pass the customer-facing hostname so a reseller's
// domain returns the reseller's OWN published packages (the API resolves
// host→reseller); platform/direct hosts return the platform catalog. Mirrors
// fetchHostBrand() — the API host differs from window.location.host.
export const listPricingPagePackages = () =>
  USE_MOCKS
    ? mockDelay(pricingPagePackages)
    : apiGetList<(typeof pricingPagePackages)[number]>(
        `/pricing?host=${encodeURIComponent(typeof window !== 'undefined' ? window.location.host : '')}`,
      );
export const listPackageCategories = () =>
  USE_MOCKS ? mockDelay(packageCategories) : apiGetList<(typeof packageCategories)[number]>('/package-categories');

/* ── Admin catalog writes (categories / add-ons / platform coupons) ────────── */
export const addPackageCategory = (input: { slug: string; displayName: string; iconKey?: string | null; provisionsPos?: boolean; isActive?: boolean }) =>
  USE_MOCKS ? mockDelay({ ...input, id: `cat_${input.slug}` }) : apiPost('/admin/package-categories', input);
export const updatePackageCategory = (slug: string, patch: { displayName?: string; iconKey?: string | null; provisionsPos?: boolean; isActive?: boolean }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/package-categories/${slug}`, patch);

/** Entitlement-boost delta on top of the package's caps — all optional positive ints, "+N" meaning. */
export type AddOnConditions = { maxProducts?: number; maxInvoices?: number; maxInvoicesYearly?: number; maxUsers?: number; maxLocations?: number };
export type AddOnInput = { name: string; description?: string; billing: 'recurring' | 'one_time'; priceMinor: number; status?: 'active' | 'draft' | 'archived'; packageIds?: string[]; conditions?: AddOnConditions | null };
export const addAddon = (input: AddOnInput) =>
  USE_MOCKS ? mockDelay({ id: 'ao_mock', name: input.name }) : apiPost('/admin/add-ons', input);
export const updateAddon = (id: string, patch: Partial<AddOnInput>) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/add-ons/${id}`, patch);

export const addAdminCoupon = (input: { code: string; discountType: 'percent' | 'fixed' | 'trial_extension' | 'free_intervals'; discountValue: number; maxRedemptions?: number | null; expiresAt?: string | null }) =>
  USE_MOCKS ? mockDelay({ id: 'cp_mock', code: input.code }) : apiPost('/admin/coupons', input);
export const setCouponStatus = (id: string, status: 'active' | 'disabled' | 'draft') =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/coupons/${id}`, { status });

export const usePackages = () => useQuery({ queryKey: ['packages'], queryFn: listPackages });
export const usePricesByPackage = () =>
  useQuery({ queryKey: ['prices', 'byPackage'], queryFn: getPricesByPackage });
/** Host-scoped catalog for buyer-facing plan pickers (see listSellablePackages). */
export const useSellablePackages = () =>
  useQuery({ queryKey: ['packages', 'sellable'], queryFn: listSellablePackages });
export const useSellablePrices = () =>
  useQuery({ queryKey: ['prices', 'sellable'], queryFn: listSellablePrices });
export const useProPrices = () => useQuery({ queryKey: ['prices', 'pro'], queryFn: getProPrices });
export const useCoupons = () => useQuery({ queryKey: ['coupons'], queryFn: listCoupons });
export const useAddons = () => useQuery({ queryKey: ['addons'], queryFn: listAddons });
export const usePricingPagePackages = () =>
  useQuery({ queryKey: ['pricing', 'packages'], queryFn: listPricingPagePackages });
export const usePackageCategories = () =>
  useQuery({ queryKey: ['packageCategories'], queryFn: listPackageCategories });
